/** One fixed simulation step for the whole city. Pure given (city, dt). Steps
 *  every building (pawns, needs, production, heat, raids) then city storyteller. */
import type { City, Building } from './types';
import { SIM } from './config';
import { stepColonist } from './movement';
import { updateNeeds, updateMood } from './needs';
import { runProduction } from './jobs';
import { applyHeat } from './heat';
import { rollRaid, resolveRaid } from './raid';
import { rollEvent } from './events';
import { checkGoals } from './progress';
import { pushLog } from './world';
import { addThought } from './colonist';

export interface StepResult {
  raid: 'none' | 'repelled' | 'hit';
}

function stepBuilding(city: City, b: Building, dt: number): 'none' | 'repelled' | 'hit' {
  for (const c of b.colonists) {
    // arrestedReturn === -1 means "gone for good" — never auto-releases
    if (c.activity === 'arrested' && c.arrestedReturn > 0 && c.arrestedReturn <= city.t) {
      c.activity = 'idle';
      c.floor = 0;
      c.x = 0.1;
      c.arrestedReturn = 0;
      pushLog(city, 'good', `${c.name} is back on the street.`);
      addThought(c, city.t, 'freed', 'Back on the street', 6);
    }
    stepColonist(city, b, c, dt);
    updateNeeds(b, c, dt);
    updateMood(city, c);

    if (c.mood < SIM.BREAK_THRESHOLD && c.breakUntil <= city.t && c.arrestedReturn <= city.t && c.downUntil <= city.t) {
      c.breakUntil = city.t + SIM.BREAK_SEC;
      pushLog(city, 'bad', `😩 ${c.name} is breaking down — needs a rest.`);
      addThought(c, city.t, 'break', 'Breaking down', -10);
    }
  }

  runProduction(city, b, dt);
  applyHeat(city, b, dt);
  const raid = resolveRaid(city, b);
  rollRaid(city, b, dt);

  b.res.product = Math.max(0, b.res.product);
  b.res.meals = Math.max(0, b.res.meals);
  return raid;
}

export function stepSim(city: City, dt: number): StepResult {
  city.t += dt;
  city.toasts = city.toasts.filter((tst) => tst.expiresAt > city.t);

  let active: 'none' | 'repelled' | 'hit' = 'none';
  for (const b of city.buildings) {
    const r = stepBuilding(city, b, dt);
    if (b.id === city.activeId && r !== 'none') active = r;
  }

  rollEvent(city, dt);
  checkGoals(city);
  city.cash = Math.max(0, city.cash);

  return { raid: active };
}
